import config from '../../../../config/api';
import { BacktestRequest } from '../config/types';
import { BacktestPhase } from './Backtest';
import logger from '../../../../logging/logger';

/**
 * Response from the backtest config API endpoint
 */
export interface BacktestConfigApiResponse {
  success: boolean;
  backtest_config_id: string;
  session_id: string;
  user_id: string;
  backtest_config: BacktestRequest | null;
  error?: string;
}

export interface BacktestNameExistsResponse {
  exists: boolean;
  backtest_name: string;
  error?: string;
}

/**
 * Response from the backtest results save API endpoint
 */
export interface BacktestResultsSaveResponse {
  success: boolean;
  backtest_config_id: string;
  results_data_id?: string;  // ID of the stored results in tool_data
  message: string;
  error?: string;
}

interface BacktestResponse {
  backtestConfigId?: string;  // Config ID from backtest_saved SSE event
  resultsDataId?: string;     // Results data_id from backtest_saved SSE event
  [key: string]: any;
}

export interface BacktestProgress {
  phase: BacktestPhase;
  phase_progress: number;   // 0-100 within current phase
  overall_progress: number; // 0-100 total progress
  current_period?: string;  // e.g., "03/2024" during execution phase
  current_symbol?: string;  // Current symbol being processed
  message: string;          // Human-readable status message
  error?: string;           // Error message if phase is 'failed'
}

/**
 * Callback for progress updates during streaming backtest
 */
export type ProgressCallback = (progress: BacktestProgress) => void;

/**
 * Callback for when job ID is received from response headers
 */
export type JobIdCallback = (jobId: string) => void;
export interface AssistantMessagePayload {
  messageId: string;
  text: string;
}
export type AssistantMessageCallback = (message: AssistantMessagePayload) => void;
export interface FailurePersistenceErrorPayload {
  backtestError: string;
  persistenceError: string;
}
export interface BacktestStreamingError extends Error {
  kind?: 'backtest_error' | 'failure_persistence_error' | 'name_conflict';
  assistantMessageReceived?: boolean;
  persistenceError?: string;
}

export interface BacktestSubmitSourceContext {
  sourceBacktestConfigId?: string;
  sourceTriggerMessageId?: string;
}


/**
 * Service for fetching AI-generated backtest configurations from the backend
 */
export class BacktestService {
  private baseUrl = config.apiUrl;
  private rustBaseUrl = config.rustApiUrl;
  private configCache = new Map<string, BacktestRequest>();

  /**
   * Retrieve backtest configuration from session storage
   */
  async getBacktestConfig(
    sessionId: string,
    backtestConfigId: string,
    userEmail: string,
    token: string
  ): Promise<BacktestRequest | null> {
    try {
      // Check cache first
      const cacheKey = `${sessionId}:${backtestConfigId}`;
      if (this.configCache.has(cacheKey)) {
        logger.info('📋 [BacktestService] Cache hit:', cacheKey);
        return this.configCache.get(cacheKey)!;
      }

      // Get user UUID from email
      const userResponse = await fetch(
        `${this.baseUrl}/user/email/${encodeURIComponent(userEmail)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!userResponse.ok) {
        logger.error('Failed to get user from database');
        return null;
      }

      const userData = await userResponse.json();
      const userUuid = userData.uuid;

      // Fetch backtest configuration
      const response = await fetch(
        `${this.baseUrl}/session/${sessionId}/backtest/${backtestConfigId}?user_id=${userUuid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn(`Backtest configuration not found: ${backtestConfigId}`);
          return null;
        }
        throw new Error(`Failed to fetch backtest configuration: ${response.statusText}`);
      }

      const result: BacktestConfigApiResponse = await response.json();

      if (!result.success || !result.backtest_config) {
        logger.error('Backtest config fetch failed:', result.error);
        return null;
      }

      // Cache the result
      this.configCache.set(cacheKey, result.backtest_config);

      logger.info('📋 [BacktestService] Fetched backtest config:', backtestConfigId);
      return result.backtest_config;
    } catch (error) {
      logger.error('Error fetching backtest configuration:', error);
      return null;
    }
  }

  /**
   * Save a backtest configuration to the backend (for manually created configs)
   * Returns the backtest_config_id that can be used to save results later
   */
  async saveBacktestConfig(
    sessionId: string,
    config: BacktestRequest,
    userUuid: string,
    token: string,
    backtestName?: string,
    overwriteExistingName: boolean = false
  ): Promise<string | null> {
    try {
      const normalizedBacktestName = backtestName?.trim().toLowerCase();
      const response = await fetch(
        `${this.baseUrl}/session/${sessionId}/backtest?user_id=${userUuid}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            backtest_config: config,
            data_description: 'User-created backtest configuration',
            backtest_name: normalizedBacktestName || undefined,
            overwrite_existing_name: overwriteExistingName,
          }),
        }
      );

      if (!response.ok) {
        logger.error('Failed to save backtest config:', response.statusText);
        return null;
      }

      const result: BacktestConfigApiResponse = await response.json();

      if (result.success && result.backtest_config_id) {
        logger.info('📋 [BacktestService] Saved backtest config:', result.backtest_config_id);
        return result.backtest_config_id;
      } else {
        logger.error('Failed to save backtest config:', result.error);
        return null;
      }
    } catch (error) {
      logger.error('Error saving backtest config:', error);
      return null;
    }
  }

  /**
   * Save backtest results to the backend
   * Results are stored as tool_data with a results_data_id reference in the config
   */
  async saveBacktestResults(
    sessionId: string,
    backtestConfigId: string,
    results: Record<string, unknown>,
    userUuid: string,
    token: string
  ): Promise<BacktestResultsSaveResponse | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/session/${sessionId}/backtest/${backtestConfigId}/results?user_id=${userUuid}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ results }),
        }
      );

      if (!response.ok) {
        logger.error('Failed to save backtest results:', response.statusText);
        return null;
      }

      const result: BacktestResultsSaveResponse = await response.json();

      if (result.success) {
        logger.info('📊 [BacktestService] Saved backtest results:', {
          backtestConfigId,
          resultsDataId: result.results_data_id,
        });
      } else {
        logger.error('Failed to save backtest results:', result.error);
      }

      return result;
    } catch (error) {
      logger.error('Error saving backtest results:', error);
      return null;
    }
  }

  /**
   * Run backtest with streaming progress and save results in a single API call.
   * Calls the Rust backend directly which handles:
   * 1. Saving the backtest config to MongoDB
   * 2. Running the backtest with streaming progress
   * 3. Saving results and linking them to the config
   * 4. Creating chat messages
   *
   * @param sessionId - Session ID
   * @param config - Backtest configuration
   * @param userUuid - User UUID
   * @param token - JWT token
   * @param onProgress - Callback for progress updates
   * @param onJobId - Optional callback for job ID from response headers
   * @returns Final backtest result data
   */
  async runBacktestWithStreaming(
    sessionId: string,
    config: BacktestRequest,
    userUuid: string,
    token: string,
    onProgress: ProgressCallback,
    onJobId?: JobIdCallback,
    backtestName?: string,
    sourceContext?: BacktestSubmitSourceContext,
    onAssistantMessage?: AssistantMessageCallback
  ): Promise<any> {
    try {
      const normalizedBacktestName = backtestName?.trim().toLowerCase();
      const response = await fetch(
        `${this.rustBaseUrl}/session/${sessionId}/backtest_and_results?user_id=${userUuid}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            backtest_config: config,
            data_description: 'User created/validated backtest configuration',
            backtest_name: normalizedBacktestName || undefined,
            source_backtest_config_id: sourceContext?.sourceBacktestConfigId,
            source_trigger_message_id: sourceContext?.sourceTriggerMessageId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const jobId = response.headers.get('X-Job-Id');
      if (jobId) {
        logger.info('📝 [BacktestService] Job ID from header:', jobId);
        onJobId?.(jobId);
      }

      if (!response.body) {
        throw new Error('Response body is null - SSE streaming not supported');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let result: BacktestResponse | null = null;
      let error: string | null = null;
      let assistantMessageReceived = false;
      let failurePersistenceError: FailurePersistenceErrorPayload | null = null;
      let nameConflict: string | null = null;
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          logger.info('📡 SSE stream completed');
          break;
        }

        // Append new data to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (separated by double newline)
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || ''; // Keep incomplete message in buffer

        for (const message of messages) {
          if (!message.trim()) continue;

          // Parse SSE format: "event: type\ndata: json"
          const lines = message.split('\n');
          let eventType = '';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              eventData = line.slice(5).trim();
            }
          }

          if (!eventData) continue;

          try {
            const parsed = JSON.parse(eventData);

            switch (eventType) {
              case 'progress':
                // Extract progress from the wrapper
                if (parsed.type === 'progress') {
                  const progress: BacktestProgress = {
                    phase: parsed.phase,
                    phase_progress: parsed.phase_progress,
                    overall_progress: parsed.overall_progress,
                    current_period: parsed.current_period,
                    current_symbol: parsed.current_symbol,
                    message: parsed.message,
                    error: parsed.error,
                  };
                  logger.info(`📊 Progress: ${progress.overall_progress.toFixed(1)}% - ${progress.message}`);
                  onProgress(progress);
                }
                break;

              case 'result':
                logger.info('✅ Backtest completed successfully');
                result = parsed.data;
                break;

              case 'backtest_saved':
                logger.info('💾 Backtest saved with data_id:', parsed.results_data_id);
                if (result) {
                  result.backtestConfigId = parsed.backtest_config_id;
                  result.resultsDataId = parsed.results_data_id;
                }
                break;

              case 'error':
                logger.error('❌ Backtest error:', parsed.message);
                error = parsed.message || 'Unknown error';
                break;

              case 'assistant_message':
                if (parsed.type === 'assistant_message' && parsed.message_id && parsed.text) {
                  assistantMessageReceived = true;
                  logger.info('💬 [BacktestService] Assistant message event:', parsed.message_id);
                  onAssistantMessage?.({
                    messageId: parsed.message_id,
                    text: parsed.text,
                  });
                }
                break;

              case 'failure_persistence_error':
                if (
                  parsed.type === 'failure_persistence_error' &&
                  parsed.backtest_error &&
                  parsed.persistence_error
                ) {
                  logger.error('❌ [BacktestService] Failure persistence error:', parsed);
                  failurePersistenceError = {
                    backtestError: parsed.backtest_error,
                    persistenceError: parsed.persistence_error,
                  };
                }
                break;

              case 'name_conflict':
                if (parsed.type === 'name_conflict' && parsed.message) {
                  logger.warn('⚠️ [BacktestService] Backtest name conflict:', parsed.message);
                  nameConflict = parsed.message;
                }
                break;

              case 'done':
                logger.info('📡 Stream done signal received');
                break;
            }
          } catch (parseError) {
            logger.warn('Failed to parse SSE event:', eventData, parseError);
          }
        }
      }

      if (nameConflict) {
        const streamError: BacktestStreamingError = new Error(nameConflict);
        streamError.kind = 'name_conflict';
        streamError.assistantMessageReceived = assistantMessageReceived;
        throw streamError;
      }

      if (failurePersistenceError) {
        const streamError: BacktestStreamingError = new Error(failurePersistenceError.backtestError);
        streamError.kind = 'failure_persistence_error';
        streamError.assistantMessageReceived = assistantMessageReceived;
        streamError.persistenceError = failurePersistenceError.persistenceError;
        throw streamError;
      }

      if (error) {
        const streamError: BacktestStreamingError = new Error(error);
        streamError.kind = 'backtest_error';
        streamError.assistantMessageReceived = assistantMessageReceived;
        throw streamError;
      }

      if (!result) {
        throw new Error('No result received from backtest stream');
      }

      return result;

    } catch (error) {
      logger.error('❌ Streaming backtest error:', error);
      throw error;
    }
  }

  async checkBacktestNameExists(
    sessionId: string,
    backtestName: string,
    userUuid: string,
    token: string
  ): Promise<boolean> {
    const normalizedName = backtestName.trim().toLowerCase();
    if (!normalizedName) {
      return false;
    }

    const query = new URLSearchParams({
      user_id: userUuid,
      backtest_name: normalizedName,
    });

    const response = await fetch(
      `${this.rustBaseUrl}/session/${sessionId}/backtest/name-exists/check?${query.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to check backtest name: ${response.status}`);
    }

    const result: BacktestNameExistsResponse = await response.json();
    if (result.error) {
      throw new Error(result.error);
    }

    return result.exists;
  }

  /**
   * Clear cached config for a session
   */
  clearCache(sessionId?: string) {
    if (sessionId) {
      // Clear only configs for this session
      const keys = Array.from(this.configCache.keys());
      keys.forEach(key => {
        if (key.startsWith(`${sessionId}:`)) {
          this.configCache.delete(key);
        }
      });
    } else {
      // Clear all
      this.configCache.clear();
    }
  }
}

// Singleton instance
export const backtestService = new BacktestService();
